import { Avatar, useMantineTheme } from "@mantine/core";
import { getContrastColor } from "../utils/color";

// First letters of (up to) the first two words of a name - "Ash Richards" -> "AR".
export const getInitials = (name) =>
  (name ?? "")
    .trim()
    .split(/\s+/)
    .map((part) => part[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

// A person's avatar, always shown as their initials rather than an image. Profile pictures aren't
// loaded anywhere in the app on purpose - every one is an extra network request on pages that can
// list dozens of people (Employees, Audit Log, Jira boards, Harcast), and initials are instant.
//
// Filled with the company's accent colour (the theme's primary shade 6 is that exact hex - see
// App.js) with the initials in black or white, whichever reads better on it. With no usable name
// this renders Mantine's own blank placeholder icon instead of an empty circle.
export function PersonAvatar({ name, radius = "xl", ...avatarProps }) {
  const theme = useMantineTheme();
  const accent = theme.colors[theme.primaryColor]?.[6] ?? theme.colors.blue[6];
  const initials = getInitials(name);
  return (
    <Avatar
      radius={radius}
      alt={name ?? undefined}
      // Mantine paints the fill on the inner placeholder element, not the root, and also gives it
      // a 1px border and its own text colour - all overridden here.
      styles={{
        placeholder: { backgroundColor: accent, color: getContrastColor(accent), borderColor: accent },
        placeholderIcon: { color: getContrastColor(accent) },
      }}
      {...avatarProps}
    >
      {initials || undefined}
    </Avatar>
  );
}
